import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Slider } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { dimGrey, lightBlack, lightGrey, lightTeal } from '../../../mainTheme';

interface PhotoUploadProps {
  onBack: () => void;
  onSave: (imageBlob: Blob, previewUrl: string) => void;
  initialPhoto?: string | null;
  isLoading?: boolean;
}

export const PhotoUpload = ({
  onBack,
  onSave,
  initialPhoto = null,
  isLoading = false,
}: PhotoUploadProps) => {
  const [photo, setPhoto] = useState(initialPhoto);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelected(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelected = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleZoomChange = (_event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setZoomLevel(newValue);
    }
  };

  const getScaledImageData = () => {
    if (!photo || !imageRef.current) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    canvas.width = 150;
    canvas.height = 150;

    if (ctx) {
      ctx.beginPath();
      ctx.arc(75, 75, 75, 0, Math.PI * 2);
      ctx.clip();

      const scale = zoomLevel / 100;
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;

      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

      return {
        blob: canvas.toBlob(
          (blob) => {
            if (blob) {
              onSave(blob, canvas.toDataURL('image/jpeg', 0.9));
            }
          },
          'image/jpeg',
          0.9,
        ),
        previewUrl: canvas.toDataURL('image/jpeg', 0.9),
      };
    }
    return null;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '22px',
            lineHeight: '30px',
            letterSpacing: '0.36px',
          }}
        >
          Add new photo
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '22px',
            letterSpacing: '0.2px',
            color: dimGrey,
            mt: 1,
          }}
        >
          Drag and drop or click to upload a new photo.
        </Typography>
      </Box>
      <Box
        sx={{
          width: '200px',
          height: '200px',
          margin: '0 auto',
          borderRadius: '50%',
          border: '1px solid',
          borderColor: isDragging ? 'primary.main' : 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: isDragging ? 'rgba(63, 81, 181, 0.05)' : '#F6F6F9',
          overflow: 'hidden',
          position: 'relative',
          mb: 3,
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {photo ? (
          <img
            ref={imageRef}
            src={photo}
            alt="Profile"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: `scale(${zoomLevel / 100})`,
            }}
          />
        ) : (
          <FontAwesomeIcon
            icon={faUserPlus}
            style={{
              width: '125px',
              height: '125px',
              color: lightGrey,
              marginLeft: 24,
            }}
          />
        )}

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleFileSelected(e.target.files[0]);
            }
          }}
        />
      </Box>

      {photo && (
        <Box sx={{ width: '80%', margin: '0 auto' }}>
          <Typography
            id="zoom-slider"
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.2px',
              color: lightBlack,
            }}
          >
            Zoom
          </Typography>
          <Slider
            value={zoomLevel}
            onChange={handleZoomChange}
            aria-labelledby="zoom-slider"
            min={50}
            max={150}
            sx={{
              '& .MuiSlider-rail': {
                backgroundColor: lightGrey,
                opacity: 1,
              },
              '& .MuiSlider-track': {
                backgroundColor: lightGrey,
                border: 'none',
              },
              '& .MuiSlider-thumb': {
                backgroundColor: lightTeal,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0px 0px 0px 8px rgba(107, 202, 186, 0.16)',
                },
                '&.Mui-active': {
                  boxShadow: '0px 0px 0px 14px rgba(107, 202, 186, 0.16)',
                },
              },
            }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          onClick={onBack}
          color="secondary"
          sx={{
            padding: '14px 24px 12px 24px',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={getScaledImageData}
          disabled={!photo || isLoading}
          sx={{
            padding: '14px 24px 12px 24px',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
